import { useCallback, useEffect, useRef, useState } from "react";
import type { AppState } from "../../shared/types";
import {
  syncSections,
  lichessSectionLabel,
  stopsLichessSync,
} from "../../shared/lichess";
import { ApiError, authHeaders, type Identity } from "./api";
interface Result {
  state?: AppState;
  connected?: boolean;
  authorizationUrl?: string;
  revoked?: boolean;
  imported?: number;
}
export async function lichessRequest(
  operation: string,
  body?: unknown,
): Promise<Result> {
  const response = await fetch(`/api/lichess/${operation}`, {
    method: body === undefined ? "GET" : "POST",
    headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok)
    throw new ApiError(
      data.error ?? "The Lichess connection could not be reached.",
      response.status,
      data.code,
    );
  return data;
}
export type LichessControls = ReturnType<typeof useLichess>;
export function useLichess(
  state: AppState,
  identity: Identity,
  ready: boolean,
  accept: (state: AppState) => void,
  flush: () => Promise<void>,
  notify: (text: string) => void,
) {
  const [connected, setConnected] = useState(false),
    [progress, setProgress] = useState(""),
    [error, setError] = useState(""),
    [remaining, setRemaining] = useState<string[]>([]),
    [failures, setFailures] = useState<{ section: string; message: string }[]>(
      [],
    );
  const running = useRef(false),
    initialized = useRef(""),
    stateRef = useRef(state),
    scopeRef = useRef("");
  stateRef.current = state;
  const scope = `${identity.storage}:${identity.id ?? ""}`;
  scopeRef.current = scope;
  const runSync = useCallback(
    async (older = false, selected?: string[]) => {
      if (running.current) return;
      running.current = true;
      setError("");
      setFailures([]);
      setRemaining([]);
      const owner = scopeRef.current;
      let imported = 0;
      const failed: { section: string; message: string }[] = [];
      const sections = selected ?? (older ? ["olderGames"] : syncSections);
      try {
        for (let i = 0; i < sections.length; i++) {
          if (scopeRef.current !== owner) return;
          const section = sections[i];
          setProgress(
            older
              ? "Importing older games…"
              : `Syncing ${lichessSectionLabel(section).toLowerCase()} · ${i + 1} of ${sections.length}`,
          );
          try {
            const result = await lichessRequest("sync", { section });
            if (scopeRef.current !== owner) return;
            if (result.state) accept(result.state);
            imported += result.imported ?? 0;
          } catch (e) {
            if (scopeRef.current !== owner) return;
            const message = e instanceof Error ? e.message : "Sync failed.";
            failed.push({ section, message });
            if (e instanceof ApiError && stopsLichessSync(e.status)) {
              if (e.status === 401) setConnected(false);
              setRemaining(sections.slice(i + 1));
              break;
            }
          }
        }
        if (failed.length) setFailures(failed);
        else
          notify(
            older
              ? `Imported ${imported} more completed games.`
              : `Lichess synced${imported ? ` · ${imported} games imported` : ""}.`,
          );
      } finally {
        running.current = false;
        if (scopeRef.current === owner) setProgress("");
      }
    },
    [accept, notify],
  );
  useEffect(() => {
    if (!ready || !identity.id || initialized.current === scope) return;
    initialized.current = scope;
    setConnected(false);
    setError("");
    setFailures([]);
    void (async () => {
      try {
        if (location.pathname === "/lichess/callback") {
          const params = new URLSearchParams(location.search),
            code = params.get("code"),
            nonce = params.get("state");
          history.replaceState(null, "", "/");
          if (params.has("error")) {
            setError(
              "Lichess linking was cancelled. You can connect again from Settings.",
            );
            notify("Lichess linking was cancelled.");
            return;
          }
          if (code && nonce) {
            setProgress("Connecting Lichess…");
            const result = await lichessRequest("complete", {
              code,
              state: nonce,
            });
            if (scopeRef.current !== scope) return;
            if (result.state) accept(result.state);
            setConnected(true);
            setProgress("");
            await runSync();
            return;
          }
        }
        const result = await lichessRequest("status");
        if (scopeRef.current !== scope) return;
        setConnected(result.connected === true);
        const snapshot = stateRef.current.lichess;
        if (
          result.connected &&
          snapshot &&
          Date.now() - (snapshot.sections.games?.at ?? 0) > 86400000
        )
          await runSync();
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Could not connect Lichess.";
        setError(message);
        notify(message);
        setProgress("");
      }
    })();
  }, [ready, identity.id, scope, accept, notify, runSync]);
  const connect = async () => {
    if (running.current) return;
    setError("");
    setFailures([]);
    setProgress("Opening Lichess…");
    try {
      await flush();
      const result = await lichessRequest("start", {});
      if (result.authorizationUrl?.startsWith("https://lichess.org/oauth?"))
        location.assign(result.authorizationUrl);
      else throw new Error("Lichess did not provide a connection link.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect.");
      setProgress("");
    }
  };
  const disconnect = async () => {
    if (running.current) return;
    setProgress("Disconnecting Lichess…");
    setError("");
    setFailures([]);
    try {
      const result = await lichessRequest("disconnect", {});
      if (result.state) accept(result.state);
      setConnected(false);
      if (!result.revoked)
        setError(
          "Disconnected here. Lichess could not revoke access; remove Cretchess under Lichess Preferences → OAuth apps.",
        );
      else
        notify("Lichess disconnected. Imported games remain in your library.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disconnect.");
    } finally {
      setProgress("");
    }
  };
  const setContext = async (enabled: boolean) => {
    try {
      const result = await lichessRequest("context", { enabled });
      if (result.state) accept(result.state);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not update coaching context.",
      );
    }
  };
  return {
    connected,
    progress,
    error,
    failures,
    paused: remaining.length > 0,
    connect,
    sync: () => runSync(),
    retry: () =>
      runSync(false, [
        ...failures.map((failure) => failure.section),
        ...remaining,
      ]),
    older: () => runSync(true),
    disconnect,
    setContext,
  };
}
